import { useState } from "react";
import { api } from "../api";
import { num, rupee, todayISO } from "../format";
import type { StrategyOption, Trade, Tradebook } from "../types";
import { EXCHANGES, PRODUCTS } from "../types";
import { PositionSizer } from "./PositionSizer";
import { SymbolSearch } from "./SymbolSearch";

interface Props {
  book: Tradebook;
  strategies: StrategyOption[];
  onCreated: (trade: Trade) => void;
  onCancel: () => void;
}

const EMPTY = {
  symbol: "",
  entry_date: todayISO(),
  quantity: "",
  price: "",
  sl_price: "",
  exchange: "NSE",
  product: "cash",
  strategy: "",
  setup_tag: "",
  thesis: "",
  conviction: "3",
};

/**
 * Manual entry — the primary way trades get in.
 *
 * The stop and thesis are asked for at entry on purpose: writing down why you
 * are taking the trade, and where you are wrong, is the part that changes
 * behaviour. The live risk preview below makes an oversized position obvious
 * before it is taken rather than after.
 */
export function NewTrade({ book, strategies, onCreated, onCancel }: Props) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSizer, setShowSizer] = useState(false);

  const set = (key: keyof typeof EMPTY, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const qty = Number(form.quantity) || 0;
  const price = Number(form.price) || 0;
  const stop = Number(form.sl_price) || 0;

  const positionValue = qty * price;
  const riskRupees = stop > 0 && price > 0 ? (price - stop) * qty : null;
  const riskPct = riskRupees !== null && book.capital_base ? (riskRupees / book.capital_base) * 100 : null;
  const budget = book.capital_base * (book.risk_per_trade_pct / 100);
  const overRisk = riskRupees !== null && riskRupees > budget;
  const overSize =
    positionValue > book.capital_base * (book.max_position_pct / 100) && book.capital_base > 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const trade = await api.createTrade({
        tradebook_id: book.id,
        symbol: form.symbol.trim(),
        entry_date: form.entry_date,
        quantity: qty,
        price,
        exchange: form.exchange,
        product: form.product,
        sl_price: form.sl_price ? Number(form.sl_price) : null,
        strategy: form.strategy || null,
        setup_tag: form.setup_tag || null,
        thesis: form.thesis || null,
        conviction: form.conviction ? Number(form.conviction) : null,
      });
      setForm(EMPTY);
      onCreated(trade);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const ready = form.symbol.trim() !== "" && qty > 0 && price > 0;

  return (
    <div className="card">
      <div className="trade-head">
        <span className="trade-symbol">New trade</span>
        <span className="badge muted">{book.name}</span>
        <div className="right">
          <button className="ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>

      {error && <div className="banner error">{error}</div>}

      <div className="grid cols-4">
        <div>
          <label>Symbol</label>
          <SymbolSearch
            autoFocus
            value={form.symbol}
            onChange={(symbol) => set("symbol", symbol)}
          />
        </div>
        <div>
          <label>Entry Date</label>
          <input
            type="date"
            value={form.entry_date}
            onChange={(e) => set("entry_date", e.target.value)}
          />
        </div>
        <div>
          <label>Quantity</label>
          <input
            type="number"
            value={form.quantity}
            onChange={(e) => set("quantity", e.target.value)}
          />
        </div>
        <div>
          <label>Entry Price</label>
          <input
            type="number"
            step="0.05"
            value={form.price}
            onChange={(e) => set("price", e.target.value)}
          />
        </div>
      </div>

      <div className="grid cols-4" style={{ marginTop: 14 }}>
        <div>
          <label>Exchange</label>
          <select value={form.exchange} onChange={(e) => set("exchange", e.target.value)}>
            {EXCHANGES.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Segment</label>
          <select value={form.product} onChange={(e) => set("product", e.target.value)}>
            {PRODUCTS.map((x) => (
              <option key={x.key} value={x.key}>
                {x.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Stop Loss</label>
          <input
            type="number"
            step="0.05"
            value={form.sl_price}
            placeholder="where you're wrong"
            onChange={(e) => set("sl_price", e.target.value)}
          />
        </div>
        <div>
          <label>Strategy</label>
          <select value={form.strategy} onChange={(e) => set("strategy", e.target.value)}>
            <option value="">—</option>
            {strategies.map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Setup Tag</label>
          <input
            value={form.setup_tag}
            placeholder="e.g. flag breakout"
            onChange={(e) => set("setup_tag", e.target.value)}
          />
        </div>
        <div>
          <label>Conviction</label>
          <select value={form.conviction} onChange={(e) => set("conviction", e.target.value)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <button className="ghost" onClick={() => setShowSizer((v) => !v)}>
          {showSizer ? "Hide" : "⊞"} position size calculator
        </button>
        {showSizer && (
          <div style={{ marginTop: 10 }}>
            <PositionSizer
              book={book}
              compact
              onApply={(quantity, entryPrice, stopPrice) => {
                setForm((f) => ({
                  ...f,
                  quantity: String(quantity),
                  price: String(entryPrice),
                  sl_price: String(stopPrice),
                }));
                setShowSizer(false);
              }}
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Thesis — why are you taking this?</label>
        <textarea
          value={form.thesis}
          placeholder="What's the setup, and what would prove you wrong?"
          onChange={(e) => set("thesis", e.target.value)}
        />
      </div>

      {(positionValue > 0 || riskRupees !== null) && (
        <div className={`banner ${overRisk || overSize ? "warn" : "ok"}`} style={{ marginTop: 14 }}>
          <div className="row" style={{ gap: 22, flexWrap: "wrap" }}>
            <span>
              Position <strong>{rupee(positionValue)}</strong>
            </span>
            {riskRupees !== null && (
              <span>
                Risk <strong>{rupee(riskRupees)}</strong>
                {riskPct !== null && ` (${num(riskPct)}% of capital)`}
              </span>
            )}
            <span className="muted">
              Book limit {book.risk_per_trade_pct}% = {rupee(budget)}
            </span>
          </div>
          {overRisk && (
            <div style={{ marginTop: 6 }}>
              ⚠ This risks more than {book.name}'s per-trade limit. Reduce size or tighten the stop.
            </div>
          )}
          {overSize && (
            <div style={{ marginTop: 6 }}>
              ⚠ Position exceeds {book.max_position_pct}% of capital.
            </div>
          )}
          {riskRupees !== null && riskRupees < 0 && (
            <div style={{ marginTop: 6 }}>
              ⚠ Your stop is above the entry price — check the numbers.
            </div>
          )}
        </div>
      )}

      <div className="row" style={{ marginTop: 16 }}>
        <button className="primary" disabled={!ready || busy} onClick={submit}>
          {busy ? "Saving…" : "Open position"}
        </button>
        <span className="hint">
          You can add fills, record exits and tick your rules once it's open.
        </span>
      </div>
    </div>
  );
}
